/**
 * Suchen und Filtern — reine Funktionen, ohne Oberflaeche.
 *
 * Der Bestand liegt komplett im Speicher (995 Kopfdatensaetze, rund 2 MB), also
 * wird bei jedem Tastendruck neu gefiltert. Das ist einfacher und schneller als
 * jeder Index, den man pflegen muesste.
 *
 * Gesucht wird ueber Platz, Notizen, Buddys, Reise, Boot, Land und Tauchgangsart
 * — also ueber das, woran man sich erinnert, wenn man einen Tauchgang sucht.
 */

/** Kleinschreibung ohne Akzente: "Ägypten" findet man auch als "agypten". */
export function normalisiere(text) {
  return String(text ?? '')
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

const SUCHFELDER = ['site_name', 'notes', 'buddy_raw', 'trip_name', 'boat', 'city',
  'country', 'dive_type', 'activity_title', 'divemaster', 'source_device'];

/** Einmal je Tauchgang: alles Durchsuchbare in einer Zeichenkette. */
export function suchtext(tg) {
  const teile = SUCHFELDER.map((feld) => tg[feld]).filter(Boolean);
  if (tg.dive_number) teile.push(String(tg.dive_number));
  return normalisiere(teile.join(' '));
}

/**
 * Die Buddys eines Tauchgangs als Namensliste.
 *
 * `buddies` ist die gepflegte Liste — beide Importer und das Formular fuellen
 * sie. `buddy_raw` ist der Rohtext daneben und der Rueckfall fuer Buendel aus
 * einer Zeit, in der die Liste noch nicht gefuellt wurde. Getrennt wird nur am
 * Komma, so wie es die Importer tun: Wer hier zusaetzlich an „und" trennte,
 * bekaeme eine Rangliste mit Namen, die es in den Stammdaten nicht gibt.
 *
 * Doppelte Nennungen im selben Tauchgang fallen weg — sonst zaehlte ein
 * Tippfehler denselben Menschen zweimal.
 */
export function buddyNamen(tg) {
  const roh = Array.isArray(tg?.buddies) && tg.buddies.length
    ? tg.buddies
    : String(tg?.buddy_raw ?? '').split(',');
  const gesehen = new Map();
  for (const eintrag of roh) {
    const name = String(eintrag ?? '').trim();
    if (!name) continue;
    const schluessel = normalisiere(name);
    if (!gesehen.has(schluessel)) gesehen.set(schluessel, name);
  }
  return [...gesehen.values()];
}

export function mitSuchtext(liste) {
  return liste.map((tg) => (tg._suche ? tg : Object.assign(tg, { _suche: suchtext(tg) })));
}

export const LEER = {
  text: '',
  jahr: null,
  platz: '',
  reise: '',
  buddy: '',
  gas: null,
  tiefeVon: null,
  tiefeBis: null,
  nurProfil: false,
  nurPruefen: false,
};

export function istLeer(kriterien) {
  return Object.entries(LEER).every(([feld, wert]) => (kriterien[feld] ?? wert) === wert);
}

function jahrVon(tg) {
  return String(tg.start_utc ?? '').slice(0, 4);
}

function gasart(tg) {
  const flasche = (tg.tanks ?? [])[0];
  if (flasche?.gas_type) return flasche.gas_type;
  const o2 = flasche?.o2_pct;
  if (typeof o2 === 'number') return o2 > 21 ? 'eanx' : 'air';
  return null;
}

export function passt(tg, kriterien = {}) {
  const k = { ...LEER, ...kriterien };

  if (k.text) {
    const wortliste = normalisiere(k.text).split(/\s+/).filter(Boolean);
    const heuhaufen = tg._suche ?? suchtext(tg);
    // Alle Begriffe muessen vorkommen — sonst findet "manta raja" alles.
    if (!wortliste.every((wort) => heuhaufen.includes(wort))) return false;
  }
  if (k.jahr && jahrVon(tg) !== String(k.jahr)) return false;
  if (k.platz && !normalisiere(tg.site_name).includes(normalisiere(k.platz))) return false;
  // Reise und Buddy werden genau verglichen, nicht als Teilzeichenkette: Beide
  // kommen aus einer Rangliste, und deren Zahl muss die Liste danach bestaetigen.
  if (k.reise && normalisiere(tg.trip_name) !== normalisiere(k.reise)) return false;
  if (k.buddy && !buddyNamen(tg).some((n) => normalisiere(n) === normalisiere(k.buddy))) {
    return false;
  }
  if (k.gas && gasart(tg) !== k.gas) return false;
  if (typeof k.tiefeVon === 'number' && !(tg.max_depth_m >= k.tiefeVon)) return false;
  if (typeof k.tiefeBis === 'number' && !(tg.max_depth_m <= k.tiefeBis)) return false;
  if (k.nurProfil && !tg.has_profile) return false;
  if (k.nurPruefen && !tg.needs_review) return false;
  return true;
}

export function filtere(liste, kriterien) {
  if (istLeer({ ...LEER, ...kriterien })) return liste;
  return liste.filter((tg) => passt(tg, kriterien));
}

/** Alle vorkommenden Jahre, neueste zuerst — fuer die Auswahlliste. */
export function jahre(liste) {
  return [...new Set(liste.map(jahrVon).filter(Boolean))].sort().reverse();
}

/** Plaetze nach Haeufigkeit, damit die Vorschlaege oben stehen, wo man taucht. */
export function plaetze(liste) {
  const zaehler = new Map();
  for (const tg of liste) {
    if (tg.site_name) zaehler.set(tg.site_name, (zaehler.get(tg.site_name) ?? 0) + 1);
  }
  return [...zaehler.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

/** Reisen, juengste zuerst — die Vorschlagsliste des Filters. */
export function reisen(liste) {
  const juengste = new Map();
  for (const tg of liste) {
    const name = String(tg.trip_name ?? '').trim();
    if (!name) continue;
    const zeit = String(tg.start_utc ?? '');
    if (zeit > (juengste.get(name) ?? '')) juengste.set(name, zeit);
  }
  return [...juengste.entries()]
    .sort((a, b) => b[1].localeCompare(a[1]) || a[0].localeCompare(b[0]))
    .map(([name]) => name);
}

/** Buddys nach Haeufigkeit — wer oft dabei ist, steht oben. */
export function buddys(liste) {
  const zaehler = new Map();
  for (const tg of liste) {
    for (const name of buddyNamen(tg)) {
      zaehler.set(name, (zaehler.get(name) ?? 0) + 1);
    }
  }
  return [...zaehler.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([name]) => name);
}

// ------------------------------------------------------------------ Adresse

/**
 * Kriterien in der Adresszeile — damit der Zurueck-Knopf funktioniert und eine
 * Suche sich weitergeben laesst.
 */
export function zuHash(kriterien) {
  const k = { ...LEER, ...kriterien };
  const teile = new URLSearchParams();
  if (k.text) teile.set('q', k.text);
  if (k.jahr) teile.set('jahr', String(k.jahr));
  if (k.platz) teile.set('platz', k.platz);
  if (k.reise) teile.set('reise', k.reise);
  if (k.buddy) teile.set('buddy', k.buddy);
  if (k.gas) teile.set('gas', k.gas);
  if (typeof k.tiefeVon === 'number') teile.set('von', String(k.tiefeVon));
  if (typeof k.tiefeBis === 'number') teile.set('bis', String(k.tiefeBis));
  if (k.nurProfil) teile.set('profil', '1');
  if (k.nurPruefen) teile.set('pruefen', '1');
  const text = teile.toString();
  return text ? `#/?${text}` : '#/';
}

export function ausHash(hash) {
  const stelle = String(hash ?? '').indexOf('?');
  if (stelle === -1) return { ...LEER };
  const teile = new URLSearchParams(String(hash).slice(stelle + 1));
  const zahl = (name) => (teile.has(name) && teile.get(name) !== ''
    ? Number(teile.get(name)) : null);
  return {
    ...LEER,
    text: teile.get('q') ?? '',
    jahr: teile.get('jahr') || null,
    platz: teile.get('platz') ?? '',
    reise: teile.get('reise') ?? '',
    buddy: teile.get('buddy') ?? '',
    gas: teile.get('gas') || null,
    tiefeVon: Number.isFinite(zahl('von')) ? zahl('von') : null,
    tiefeBis: Number.isFinite(zahl('bis')) ? zahl('bis') : null,
    nurProfil: teile.get('profil') === '1',
    nurPruefen: teile.get('pruefen') === '1',
  };
}

/** Kurzfassung der aktiven Filter — fuer die Zeile ueber den Ergebnissen. */
export function beschreibung(kriterien) {
  const k = { ...LEER, ...kriterien };
  const teile = [];
  if (k.text) teile.push(`„${k.text}“`);
  if (k.jahr) teile.push(k.jahr);
  if (k.platz) teile.push(k.platz);
  if (k.reise) teile.push(`Reise ${k.reise}`);
  if (k.buddy) teile.push(`mit ${k.buddy}`);
  if (k.gas) teile.push({ air: 'Luft', eanx: 'Nitrox', trimix: 'Trimix' }[k.gas] ?? k.gas);
  if (typeof k.tiefeVon === 'number' || typeof k.tiefeBis === 'number') {
    teile.push(`${k.tiefeVon ?? 0}–${k.tiefeBis ?? '∞'} m`);
  }
  if (k.nurProfil) teile.push('mit Profil');
  if (k.nurPruefen) teile.push('zu prüfen');
  return teile.join(' · ');
}
